/**
 * Infographic Syntax Definitions for Help Center
 * 
 * This file contains all the infographic syntax strings used throughout
 * the Help Center documentation to educate users about consent management,
 * privacy laws, and platform features.
 */

// ===== PLATFORM OVERVIEW =====

export const PLATFORM_OVERVIEW_SYNTAX = `
infographic list-grid-badge-card
data
  title TGP Consent Platform
  desc Comprehensive Digital Consent Management System
  items
    - label Tenant Management
      desc Multi-tenant architecture with isolated data
      icon mdi/domain
    - label Consent Collection
      desc Digital consent forms with multiple evidence types
      icon mdi/file-document-check
    - label Compliance Engine
      desc DPDP, GDPR, CCPA, LGPD compliant
      icon mdi/scale-balance
    - label Data Fiduciary
      desc Organizations collecting and processing data
      icon mdi/office-building
    - label Data Owner Portal
      desc Individuals providing and managing consent
      icon mdi/account
    - label Analytics & Audit
      desc Complete tracking and compliance reporting
      icon mdi/chart-line
`;

export const CONSENT_FLOW_SYNTAX = `
infographic list-row-horizontal-icon-arrow
data
  title Consent Collection Process
  desc Step-by-step journey of consent collection
  items
    - label Create Project
      desc Define data collection purpose
      icon mdi/folder-plus
    - label Setup Templates
      desc Create consent documents
      icon mdi/file-document-edit
    - label Generate URL
      desc Create consent link
      icon mdi/link-variant
    - label Send to Owner
      desc Share via Email/SMS
      icon mdi/send
    - label Collect Evidence
      desc Signature, OTP, Video
      icon mdi/shield-check
    - label Store Record
      desc Secure audit trail
      icon mdi/database-check
`;

export const WITHDRAWAL_FLOW_SYNTAX = `
infographic list-row-horizontal-icon-arrow
data
  title Consent Withdrawal Process
  desc How data owners can withdraw their consent
  items
    - label Request
      desc Submit withdrawal
      icon mdi/file-cancel
    - label Verify
      desc Confirm identity
      icon mdi/account-check
    - label Review
      desc Admin reviews
      icon mdi/eye
    - label Process
      desc Approve/reject
      icon mdi/check-decagram
    - label Notify
      desc Inform owner
      icon mdi/bell
    - label Update
      desc Mark withdrawn
      icon mdi/database-edit
`;

// ===== KEY CONCEPTS =====

export const KEY_CONCEPTS_SYNTAX = `
infographic list-grid-badge-card
data
  title Key Platform Concepts
  desc Understanding the building blocks of consent management
  items
    - label Tenant
      desc Your organization using the platform. Each tenant has isolated data and custom branding.
      icon mdi/domain
    - label Data Fiduciary
      desc Entity that determines the purpose of data processing. Legally responsible for compliance.
      icon mdi/shield-account
    - label Data Owner
      desc Individual whose personal data is being collected. Also called Data Principal under DPDP.
      icon mdi/account
    - label Project
      desc Specific initiative requiring consent collection. Groups related templates together.
      icon mdi/folder
    - label Template
      desc Consent document with legal text, privacy policy, or terms of service.
      icon mdi/file-document
    - label Consent Form
      desc Generated URL combining templates with evidence requirements.
      icon mdi/form-select
    - label Evidence
      desc Proof of consent - signature, OTP, video recording, or audio.
      icon mdi/certificate
    - label Consent Artifact
      desc Permanent proof of consent with full audit trail (per Meity BRD).
      icon mdi/history
`;

export const TENANT_HIERARCHY_SYNTAX = `
infographic hierarchy-tree-curved-line-rounded-rect-node
data
  title Tenant Organization Structure
  desc How organizations are structured in the platform
  items
    - label Tenant (Organization)
      desc HealthCare Plus Hospital
      icon mdi/hospital-building
      children
        - label Data Fiduciary 1
          desc Hospital Admin
          icon mdi/account-tie
          children
            - label Project A
              desc Patient Registration
              icon mdi/folder
            - label Project B
              desc Clinical Trials
              icon mdi/flask
        - label Data Fiduciary 2
          desc Insurance Partner
          icon mdi/shield
          children
            - label Project C
              desc Claims Processing
              icon mdi/file-document
`;

// ===== PRIVACY LAWS =====

export const PRIVACY_LAWS_COMPARISON_SYNTAX = `
infographic compare-binary-horizontal-badge-card-arrow
data
  title DPDP Act vs GDPR
  desc Key differences between Indian and EU privacy laws
  items
    - label DPDP Act 2023
      desc India's Digital Personal Data Protection
      icon mdi/map-marker
      children
        - label Consent Age
          desc 18 years minimum
        - label Penalty
          desc Up to ₹250 Crore
        - label DPO Required
          desc For Significant Data Fiduciaries
        - label Consent Type
          desc Explicit opt-in required
    - label GDPR
      desc European General Data Protection Regulation
      icon mdi/earth
      children
        - label Consent Age
          desc 16 years minimum
        - label Penalty
          desc Up to €20M or 4% turnover
        - label DPO Required
          desc For large-scale processing
        - label Consent Type
          desc Explicit opt-in required
`;

export const GLOBAL_PRIVACY_LAWS_SYNTAX = `
infographic list-row-horizontal-icon-arrow
data
  title Global Privacy Laws Overview
  desc Major data protection regulations worldwide
  items
    - label DPDP Act 2023
      desc India - Digital Personal Data Protection
      icon mdi/flag-variant
    - label GDPR
      desc European Union - General Data Protection
      icon mdi/earth
    - label CCPA/CPRA
      desc California - Consumer Privacy Act
      icon mdi/star
    - label LGPD
      desc Brazil - General Data Protection Law
      icon mdi/leaf
    - label PDPL
      desc UAE - Personal Data Protection Law
      icon mdi/city
    - label Privacy Act
      desc Australia - Privacy Amendment Act
      icon mdi/kangaroo
`;

export const DPDP_RIGHTS_SYNTAX = `
infographic list-grid-ribbon-card
data
  title Data Principal Rights under DPDP Act
  desc Rights granted to individuals under India's privacy law
  items
    - label Right to Access
      desc Request a copy of all personal data held
      icon mdi/eye
    - label Right to Correction
      desc Request correction of inaccurate data
      icon mdi/pencil
    - label Right to Erasure
      desc Request deletion of personal data
      icon mdi/delete
    - label Right to Withdraw
      desc Withdraw previously given consent
      icon mdi/undo
    - label Right to Grievance
      desc File complaints with Data Fiduciary
      icon mdi/alert-circle
    - label Right to Nominate
      desc Designate nominee for data access
      icon mdi/account-switch
`;

// ===== EVIDENCE TYPES =====

export const EVIDENCE_TYPES_SYNTAX = `
infographic list-grid-candy-card-lite
data
  title Evidence Types for Consent
  desc Different methods to capture proof of consent
  items
    - label Digital Signature
      desc Capture drawn signature on any device
      icon mdi/draw
    - label Phone OTP
      desc Verify via SMS one-time password
      icon mdi/cellphone-message
    - label Email OTP
      desc Verify via email one-time password
      icon mdi/email-check
    - label Video Recording
      desc Record consent acknowledgment video
      icon mdi/video
    - label Audio Recording
      desc Record voice consent acknowledgment
      icon mdi/microphone
    - label Checkbox
      desc Simple checkbox acceptance
      icon mdi/checkbox-marked
`;

// ===== USER ROLES =====

export const USER_ROLES_SYNTAX = `
infographic sequence-pyramid-simple
data
  title User Roles & Permissions
  desc Hierarchical access control in the platform
  items
    - label ADMIN
      desc Full access to all features including user management
      icon mdi/shield-crown
    - label CONSENT_MANAGER
      desc Manage templates, projects, and consent artifacts
      icon mdi/shield-check
    - label DATA_CONTROLLER
      desc View records, generate forms, view analytics
      icon mdi/account-eye
    - label VIEWER
      desc Read-only access to consent artifacts
      icon mdi/eye
`;

// ===== DOCUMENT TYPES =====

export const DOCUMENT_TYPES_SYNTAX = `
infographic list-grid-compact-card
data
  title Consent Document Types
  desc Supported document types for comprehensive compliance
  items
    - label Privacy Policy
      desc Comprehensive data handling disclosure
      icon mdi/file-document
    - label Consent Text
      desc Specific consent language
      icon mdi/file-check
    - label Terms of Service
      desc Legal agreement and usage terms
      icon mdi/gavel
    - label GDPR Notice
      desc EU data protection rights
      icon mdi/earth
    - label CCPA Disclosure
      desc California privacy rights
      icon mdi/star-circle
    - label HIPAA Authorization
      desc Healthcare data authorization
      icon mdi/hospital-box
    - label Data Sharing Agreement
      desc Third-party sharing terms
      icon mdi/share-variant
    - label Cookie Policy
      desc Website tracking disclosure
      icon mdi/cookie
`;

// ===== COMPLIANCE CHECKLIST =====

export const COMPLIANCE_CHECKLIST_SYNTAX = `
infographic list-column-done-list
data
  title Compliance Checklist
  desc Ensure your consent collection is compliant
  items
    - label Define clear purpose
      desc State specific data usage
      done true
    - label Use plain language
      desc Avoid legal jargon
      done true
    - label Obtain explicit consent
      desc No pre-checked boxes
      done true
    - label Provide opt-out option
      desc Easy withdrawal process
      done true
    - label Store evidence securely
      desc Maintain audit trail
      done true
    - label Respect data rights
      desc Honor access and deletion requests
      done true
`;

// ===== PLATFORM FEATURES =====

export const PLATFORM_FEATURES_SYNTAX = `
infographic list-sector-plain-text
data
  title Platform Features
  desc Comprehensive consent management capabilities
  items
    - label Multi-Tenant
      desc Isolated data for each organization
    - label Template Library
      desc Pre-built industry templates
    - label Analytics Dashboard
      desc Track consent trends and metrics
    - label Audit Logging
      desc Complete activity tracking
    - label API Integration
      desc RESTful APIs for automation
    - label Multi-Evidence
      desc Signature, OTP, Video, Audio
    - label Withdrawal Management
      desc Handle consent withdrawals
    - label Export & Reports
      desc CSV/PDF compliance exports
`;

// ===== GETTING STARTED STEPS =====

export const GETTING_STARTED_SYNTAX = `
infographic sequence-roadmap-vertical-simple
data
  title Getting Started Guide
  desc Your journey to compliant consent collection
  items
    - label Sign Up
      desc Create your organization account
      icon mdi/account-plus
    - label Setup Profile
      desc Configure organization details
      icon mdi/cog
    - label Create Fiduciary
      desc Add data collection entity
      icon mdi/office-building
    - label Create Project
      desc Define consent collection purpose
      icon mdi/folder-plus
    - label Build Templates
      desc Create consent documents
      icon mdi/file-document-edit
    - label Generate Forms
      desc Create shareable consent URLs
      icon mdi/link-variant
    - label Collect Consent
      desc Start gathering digital consent
      icon mdi/checkbox-multiple-marked
    - label Monitor & Report
      desc Track and export records
      icon mdi/chart-line
`;

// ===== SECURITY FEATURES =====

export const SECURITY_FEATURES_SYNTAX = `
infographic list-grid-badge-card
data
  title Security & Privacy Features
  desc Enterprise-grade security for your consent data
  items
    - label Encryption
      desc AES-256 encryption at rest and in transit
      icon mdi/lock
    - label MFA Support
      desc Multi-factor authentication for admins
      icon mdi/two-factor-authentication
    - label Role-Based Access
      desc Granular permission controls
      icon mdi/shield-account
    - label Audit Trails
      desc Complete activity logging
      icon mdi/history
    - label Data Isolation
      desc Multi-tenant data separation
      icon mdi/database-lock
    - label Compliance Ready
      desc DPDP, GDPR, CCPA compliant
      icon mdi/certificate
`;

const infographicData = {
  PLATFORM_OVERVIEW_SYNTAX,
  CONSENT_FLOW_SYNTAX,
  WITHDRAWAL_FLOW_SYNTAX,
  KEY_CONCEPTS_SYNTAX,
  TENANT_HIERARCHY_SYNTAX,
  PRIVACY_LAWS_COMPARISON_SYNTAX,
  GLOBAL_PRIVACY_LAWS_SYNTAX,
  DPDP_RIGHTS_SYNTAX,
  EVIDENCE_TYPES_SYNTAX,
  USER_ROLES_SYNTAX,
  DOCUMENT_TYPES_SYNTAX,
  COMPLIANCE_CHECKLIST_SYNTAX,
  PLATFORM_FEATURES_SYNTAX,
  GETTING_STARTED_SYNTAX,
  SECURITY_FEATURES_SYNTAX,
};

export default infographicData;
